import React, { useState, useEffect } from 'react';
import { SleeperAvatar, PlayerHeadshot } from '../../shell/SleeperAvatar';

/**
 * rmtContext — full league/team/manager context for Rate My Trade surfaces,
 * ported from the legacy RateMyTradePage and restyled for the shell (light).
 * Everything reads from trade.cachedTeamData: { teamA, teamB, allTeams, league }.
 */

export const POS_ORDER = ['QB', 'RB', 'WR', 'TE', 'K', 'DEF'];

export const sortPlayers = (arr) =>
  [...(arr || [])].sort((a, b) => {
    const ai = POS_ORDER.indexOf(a.position), bi = POS_ORDER.indexOf(b.position);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi) || (a.name || '').localeCompare(b.name || '');
  });

export const ordinal = (n) => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
};

export const leagueFormatPills = (league) => {
  if (!league) return [];
  const pills = [];
  pills.push(league.isSuperFlex ? 'Superflex' : '1QB');
  const starters = (league.rosterPositions || []).filter((p) => p !== 'BN' && p !== 'IR').length;
  if (starters) pills.push(`Start ${starters}`);
  if (league.numTeams) pills.push(`${league.numTeams} teams`);
  const ppr = league.ppr;
  if (ppr === 1) pills.push('PPR');
  else if (ppr === 0.5) pills.push('0.5 PPR');
  else if (ppr === 0) pills.push('Standard');
  if (league.tePremium) pills.push(`+${league.tePremium} TEP`);
  return pills;
};

/* When the current season is all 0-0 (offseason), fall back to last season's
   records — and label which season the numbers belong to. */
export const recordContext = (allTeams, league) => {
  const teams = allTeams || [];
  const allZero = teams.every((t) => !t.record || (t.record.wins || 0) + (t.record.losses || 0) === 0);
  const hasPrev = teams.some((t) => t.prevRecord);
  const useLastSeason = allZero && hasPrev;
  const getRecord = (t) => ((useLastSeason && t.prevRecord) ? t.prevRecord : t.record);
  const recordSeason = useLastSeason
    ? String(Number(league?.season || '2026') - 1)
    : (league?.season || '');
  return { getRecord, recordSeason };
};

export const teamRank = (team, allTeams, getRecord) => {
  if (!allTeams?.length || !team) return null;
  const fn = getRecord || ((t) => t.record);
  const sorted = [...allTeams].sort((a, b) => {
    const recA = fn(a) || { wins: 0, losses: 0 };
    const recB = fn(b) || { wins: 0, losses: 0 };
    if (recA.wins !== recB.wins) return recB.wins - recA.wins;
    return recA.losses - recB.losses;
  });
  const idx = sorted.findIndex((t) => t.rosterId === team.rosterId);
  return idx === -1 ? null : idx + 1;
};

export function formatPickDisplay(pk) {
  if (!pk) return '';
  const ov = pk.override;
  let core;
  if (ov?.type === 'exact') {
    core = `${pk.season} ${pk.round}.${String(ov.value).padStart(2, '0')}`;
  } else if (ov?.type === 'tier') {
    core = `${pk.season} ${ordinal(pk.round)} (${ov.value})`;
  } else {
    return pk.displayName || '';
  }
  if (!pk.isOwn && pk.originalOwnerName) core += ` · via ${pk.originalOwnerName}`;
  return core;
}

const compactPickLabel = (pk) => {
  const ov = pk.override;
  if (ov?.type === 'exact') return `${pk.round}.${String(ov.value).padStart(2, '0')}`;
  if (ov?.type === 'tier') return `${ordinal(pk.round)} (${ov.value})`;
  if (pk.slot) return `${pk.round}.${String(pk.slot).padStart(2, '0')}`;
  if (pk.projectedSlot) return `~${pk.round}.${String(pk.projectedSlot).padStart(2, '0')}`;
  return ordinal(pk.round);
};

/* Picks whose rookie draft already ran are dead inventory — hide those seasons.
   Old cached trades have no league.draftedSeasons: show everything, as before. */
const groupPicksByYear = (picks, draftedSeasons) => {
  const drafted = new Set((draftedSeasons || []).map(String));
  const groups = {};
  (picks || []).forEach((pk) => {
    if (drafted.has(String(pk.season))) return;
    const y = pk.season;
    if (!groups[y]) groups[y] = [];
    groups[y].push(pk);
  });
  return Object.entries(groups)
    .sort(([a], [b]) => Number(a) - Number(b))
    .map(([year, items]) => ({
      year,
      items: items.sort((a, b) => {
        if (a.round !== b.round) return a.round - b.round;
        return (a.slot || a.projectedSlot || 99) - (b.slot || b.projectedSlot || 99);
      }),
    }));
};

const BASE_SLOTS = ['QB', 'RB', 'WR', 'TE', 'K', 'DEF'];

const normalizeSlot = (s) => {
  if (BASE_SLOTS.includes(s)) return s;
  if (s === 'SUPER_FLEX') return 'SUPERFLEX';
  if (String(s).includes('FLEX')) return 'FLEX';
  return s;
};

// Lineup order: base positions (only those in the league), then FLEX, then SUPERFLEX, then anything else
const orderSlotGroups = (groups) => {
  const labels = Object.keys(groups);
  const ordered = [
    ...POS_ORDER.filter((l) => labels.includes(l)),
    ...(labels.includes('FLEX') ? ['FLEX'] : []),
    ...(labels.includes('SUPERFLEX') ? ['SUPERFLEX'] : []),
    ...labels.filter((l) => !POS_ORDER.includes(l) && l !== 'FLEX' && l !== 'SUPERFLEX'),
  ];
  return ordered.map((label) => ({ label, players: groups[label] }));
};

export const ktcOf = (p, isSuperFlex) => (isSuperFlex ? p?.ktcValueSf : p?.ktcValue1qb) || 0;

/* Bench order: KTC descending; players without a KTC value sink to the bottom
   (then position order). Old cached trades carry no KTC — pure position sort. */
const sortBench = (arr, isSuperFlex) =>
  [...(arr || [])].sort((a, b) => {
    const ka = ktcOf(a, isSuperFlex), kb = ktcOf(b, isSuperFlex);
    if (ka && kb) return kb - ka;
    if (ka) return -1;
    if (kb) return 1;
    const ai = POS_ORDER.indexOf(a.position), bi = POS_ORDER.indexOf(b.position);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi) || (a.name || '').localeCompare(b.name || '');
  });

/* Returns { starterGroups: [{ label, players }], bench } — starters grouped by
   LINEUP SLOT (QB/RB/WR/TE/K/DEF, then FLEX, then SUPERFLEX), not raw position. */
function splitRosterIntoStartersBench(team, rosterPositions, isSuperFlex) {
  const players = sortPlayers(team.players || []);
  const slots = (rosterPositions || []).filter((s) => s !== 'BN' && s !== 'IR');
  const rawStarterIds = team.starterIds || [];
  const validStarterIds = rawStarterIds.filter((id) => id && id !== '0');

  const groups = {};
  const used = new Set();
  const pushTo = (label, p) => {
    if (!groups[label]) groups[label] = [];
    groups[label].push(p);
    used.add(p.id);
  };

  if (validStarterIds.length > 0) {
    const playerById = {};
    players.forEach((p) => { playerById[p.id] = p; });
    if (slots.length === rawStarterIds.length) {
      // Sleeper's starters array aligns positionally with the lineup slots ('0' = empty slot)
      rawStarterIds.forEach((id, i) => {
        if (!id || id === '0') return;
        const p = playerById[id];
        if (p) pushTo(normalizeSlot(slots[i]), p);
      });
    } else {
      // Slot order unknown — group actual starters by raw position
      players.forEach((p) => {
        if (validStarterIds.includes(p.id)) pushTo(p.position || '?', p);
      });
    }
  } else if (slots.length) {
    // No saved lineup — infer one, best available (by KTC when present) per slot
    const posCount = {};
    let flexCount = 0, superFlexCount = 0;
    slots.forEach((s) => {
      if (BASE_SLOTS.includes(s)) posCount[s] = (posCount[s] || 0) + 1;
      else if (s === 'SUPER_FLEX') superFlexCount++;
      else if (String(s).includes('FLEX')) flexCount++;
    });
    const take = (eligible, label) => {
      let best = null;
      players.forEach((pl) => {
        if (used.has(pl.id) || !eligible.includes(pl.position)) return;
        if (!best || ktcOf(pl, isSuperFlex) > ktcOf(best, isSuperFlex)) best = pl;
      });
      if (best) pushTo(label, best);
    };
    BASE_SLOTS.forEach((pos) => {
      const n = posCount[pos] || 0;
      for (let i = 0; i < n; i++) take([pos], pos);
    });
    for (let i = 0; i < flexCount; i++) take(['RB', 'WR', 'TE'], 'FLEX');
    for (let i = 0; i < superFlexCount; i++) take(['QB', 'RB', 'WR', 'TE'], 'SUPERFLEX');
  } else {
    // No lineup info at all — everyone is a "starter", grouped by position
    players.forEach((p) => pushTo(p.position || '?', p));
  }

  return {
    starterGroups: orderSlotGroups(groups),
    bench: sortBench(players.filter((p) => !used.has(p.id)), isSuperFlex),
  };
}

const PlayerRow = ({ p, ktc }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>
    <PlayerHeadshot playerId={p.id} size={22} />
    <span style={{ fontSize: 13.5, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</span>
    {p.team ? <span style={{ fontSize: 11, color: 'var(--ft-ink-3)', flex: 'none' }}>{p.team}</span> : null}
    {ktc ? (
      <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--ft-ink-3)', fontVariantNumeric: 'tabular-nums', flex: 'none' }}>
        {ktc}
      </span>
    ) : null}
  </div>
);

const PosGroup = ({ pos, players }) => (
  <div style={{ marginBottom: 14 }}>
    <div style={{
      fontSize: 10, fontWeight: 700, color: 'var(--ft-ink-3)', letterSpacing: '1.5px',
      marginBottom: 6, paddingBottom: 4, borderBottom: '1px solid var(--ft-hairline)',
    }}>
      {pos}
    </div>
    {players.map((p) => <PlayerRow key={p.id} p={p} />)}
  </div>
);

export function RosterAccordion({ team, label, league }) {
  const [open, setOpen] = useState(false);
  const isSuperFlex = !!league?.isSuperFlex;
  const { starterGroups, bench } = splitRosterIntoStartersBench(team, league?.rosterPositions || [], isSuperFlex);
  const hasBenchSplit = bench.length > 0;
  const pickGroups = groupPicksByYear(team.picks || [], league?.draftedSeasons);

  const renderGroups = (groups) => groups.map((g) => <PosGroup key={g.label} pos={g.label} players={g.players} />);

  return (
    <div style={{ borderTop: '1px solid var(--ft-hairline)' }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '16px 0', background: 'none',
        }}
      >
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 9, minWidth: 0 }}>
          <SleeperAvatar id={team.avatar} name={team.ownerName} size={22} />
          <span className="ft-k" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {label} full roster
          </span>
        </span>
        <span style={{
          fontSize: 17, color: 'var(--ft-ink-3)', transition: 'transform .2s',
          transform: open ? 'rotate(90deg)' : 'none', display: 'inline-block',
        }}>›</span>
      </button>

      {open && (
        <div style={{ paddingBottom: 22 }}>
          {hasBenchSplit ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '0 28px' }}>
              <div>
                <span className="ft-k" style={{ display: 'block', margin: '4px 0 10px' }}>Starters</span>
                {renderGroups(starterGroups)}
              </div>
              <div>
                <span className="ft-k" style={{ display: 'block', margin: '4px 0 10px' }}>Bench</span>
                {bench.map((p) => <PlayerRow key={p.id} p={p} ktc={null} />)}
              </div>
            </div>
          ) : (() => {
            const mid = Math.ceil(starterGroups.length / 2);
            return (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '0 28px' }}>
                <div>{renderGroups(starterGroups.slice(0, mid))}</div>
                <div>{renderGroups(starterGroups.slice(mid))}</div>
              </div>
            );
          })()}

          {pickGroups.length > 0 && (
            <>
              <span className="ft-k" style={{ display: 'block', margin: '14px 0 10px' }}>Draft picks</span>
              <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(pickGroups.length, 4)}, 1fr)`, gap: 18 }}>
                {pickGroups.map(({ year, items }) => (
                  <div key={year}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ft-ink-2)', marginBottom: 6 }}>{year}</div>
                    {items.map((pk) => (
                      <div
                        key={pk.id || `${pk.season}-${pk.round}-${pk.originalRosterId}`}
                        style={{ fontSize: 13, color: 'var(--ft-ink-2)', lineHeight: 1.45, marginBottom: 3 }}
                      >
                        {compactPickLabel(pk)}
                        {!pk.isOwn && pk.originalOwnerName ? (
                          <span style={{ color: 'var(--ft-ink-3)', fontSize: 11.5, marginLeft: 6 }}>via {pk.originalOwnerName}</span>
                        ) : null}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

const PLAYOFF_LABEL = { champion: 'Champion', finalist: 'Runner-up', made_playoffs: 'Made playoffs' };
const PLAYOFF_RANK = { champion: 0, finalist: 1, made_playoffs: 2 };

/* ——— Team breakdown popup (Simulate TeamDetailModal pattern) ——— */

const STRENGTH_POSITIONS = ['QB', 'RB', 'WR', 'TE'];

const rosterValue = (team, sf) => (team.players || []).reduce((s, p) => s + ktcOf(p, sf), 0);
const positionValue = (team, pos, sf) =>
  (team.players || []).filter((p) => p.position === pos).reduce((s, p) => s + ktcOf(p, sf), 0);

const rankOf = (values, mine) => {
  const idx = [...values].sort((a, b) => b - a).findIndex((v) => v === mine);
  return idx === -1 ? null : idx + 1;
};

const StrengthRow = ({ pos, value, max, rank }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
    <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--ft-ink-3)', width: 28, flex: 'none', letterSpacing: '.5px' }}>{pos}</span>
    <div style={{ flex: 1, height: 7, background: 'var(--ft-surface)', borderRadius: 4, overflow: 'hidden' }}>
      <div style={{
        height: '100%', width: `${max ? Math.max(2, Math.round((value / max) * 100)) : 0}%`,
        background: 'var(--ft-brand)', borderRadius: 4, transition: 'width .5s cubic-bezier(0.16,1,0.3,1)',
      }} />
    </div>
    <span style={{ fontSize: 12.5, fontWeight: 600, width: 44, textAlign: 'right', flex: 'none', color: 'var(--ft-ink)' }}>
      {rank ? ordinal(rank) : '—'}
    </span>
  </div>
);

const pickYearSummary = (items) => {
  const counts = {};
  items.forEach((pk) => { counts[pk.round] = (counts[pk.round] || 0) + 1; });
  return Object.entries(counts)
    .sort(([a], [b]) => Number(a) - Number(b))
    .map(([round, count]) => (count > 1 ? `${count}× ${ordinal(Number(round))}` : ordinal(Number(round))))
    .join(', ');
};

function TeamBreakdownModal({ team: t, allTeams, league, getRecord, recordSeason, regularSorted, inTrade, onOwner, onClose }) {
  const sf = !!league?.isSuperFlex;
  const rec = getRecord(t);
  const rankIdx = regularSorted.findIndex((x) => x.rosterId === t.rosterId);
  const finish = [
    rankIdx !== -1 ? `${ordinal(rankIdx + 1)} of ${regularSorted.length}` : null,
    PLAYOFF_LABEL[t.playoffResult] || null,
  ].filter(Boolean).join(' · ');

  const hasKtc = (t.players || []).some((p) => ktcOf(p, sf));
  const myTotal = rosterValue(t, sf);
  const totalRank = hasKtc ? rankOf((allTeams || []).map((x) => rosterValue(x, sf)), myTotal) : null;
  const strength = hasKtc
    ? STRENGTH_POSITIONS.map((pos) => {
        const values = (allTeams || []).map((x) => positionValue(x, pos, sf));
        const mine = positionValue(t, pos, sf);
        return { pos, value: mine, max: Math.max(...values, 1), rank: rankOf(values, mine) };
      })
    : [];
  const topPlayers = hasKtc ? sortBench(t.players || [], sf).slice(0, 5) : [];
  const pickGroups = groupPicksByYear(t.picks || [], league?.draftedSeasons);

  const line = (label, value) => (
    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 16, padding: '6px 0' }}>
      <span className="ft-k" style={{ color: 'var(--ft-ink-3)' }}>{label}</span>
      <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--ft-ink)', textAlign: 'right' }}>{value}</span>
    </div>
  );

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 120, background: 'rgba(0,0,0,0.35)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#FFFFFF', borderRadius: 18, maxWidth: 440, width: '100%',
          maxHeight: '86vh', overflowY: 'auto',
          boxShadow: '0 24px 64px rgba(0,0,0,0.18)',
        }}
      >
        <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid var(--ft-hairline)', display: 'flex', alignItems: 'center', gap: 13 }}>
          <SleeperAvatar id={t.avatar} name={t.ownerName} size={44} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 17, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {t.ownerName}
              {inTrade ? (
                <span className="ft-chip brand" style={{ fontSize: 10, padding: '2px 8px', marginLeft: 8, verticalAlign: 'middle' }}>In this trade</span>
              ) : null}
            </div>
            {t.teamName && t.teamName !== t.ownerName ? (
              <div style={{ fontSize: 12.5, color: 'var(--ft-ink-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {t.teamName}
              </div>
            ) : null}
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{ background: 'none', color: 'var(--ft-ink-3)', fontSize: 24, lineHeight: 1, padding: '0 2px', flex: 'none' }}
          >
            ×
          </button>
        </div>

        <div style={{ padding: '16px 24px 22px', display: 'flex', flexDirection: 'column', gap: 18 }}>
          <div>
            {line(recordSeason ? `${recordSeason} record` : 'Record', rec ? `${rec.wins}-${rec.losses}` : '—')}
            {finish ? line('Finish', finish) : null}
          </div>

          {hasKtc && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}>
                <span className="ft-k" style={{ color: 'var(--ft-ink-3)' }}>Roster strength</span>
                <span style={{ fontSize: 12.5, color: 'var(--ft-ink-3)' }}>
                  {totalRank ? <><b style={{ color: 'var(--ft-ink)', fontWeight: 600 }}>{ordinal(totalRank)}</b> of {allTeams.length}</> : null}
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
                {strength.map((s) => (
                  <StrengthRow key={s.pos} pos={s.pos} value={s.value} max={s.max} rank={s.rank} />
                ))}
              </div>
            </div>
          )}

          {topPlayers.length > 0 && (
            <div>
              <span className="ft-k" style={{ color: 'var(--ft-ink-3)', display: 'block', marginBottom: 4 }}>Top of roster</span>
              {topPlayers.map((p) => (
                <PlayerRow key={p.id} p={p} ktc={null} />
              ))}
            </div>
          )}

          {pickGroups.length > 0 && (
            <div>
              <span className="ft-k" style={{ color: 'var(--ft-ink-3)', display: 'block', marginBottom: 2 }}>Draft capital</span>
              {pickGroups.map(({ year, items }) => (
                <React.Fragment key={year}>{line(year, pickYearSummary(items))}</React.Fragment>
              ))}
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, marginTop: 2 }}>
            {onOwner && t.ownerName ? (
              <button className="ft-btn sm" onClick={() => onOwner(t.ownerName)}>Full intel</button>
            ) : null}
            <button className="ft-btn sm ghost" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function SeasonRecap({ allTeams, league, trade, getRecord, recordSeason, onOwner }) {
  const [popupTeam, setPopupTeam] = useState(null);

  useEffect(() => {
    if (!popupTeam) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setPopupTeam(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [popupTeam]);

  if (!allTeams?.length) return null;
  const isA = (t) => t.rosterId === trade.teamARosterId;
  const isB = (t) => t.rosterId === trade.teamBRosterId;
  const highlighted = (t) => isA(t) || isB(t);
  const sideTag = (t) => (isA(t) || isB(t) ? (
    <span className="ft-chip brand" style={{ fontSize: 10, padding: '2px 8px' }}>In this trade</span>
  ) : null);

  const regularSorted = [...allTeams].sort((a, b) => {
    const recA = getRecord(a) || { wins: 0, losses: 0 };
    const recB = getRecord(b) || { wins: 0, losses: 0 };
    if (recA.wins !== recB.wins) return recB.wins - recA.wins;
    return recA.losses - recB.losses;
  });
  const mid = Math.ceil(regularSorted.length / 2);

  const playoffTeams = allTeams
    .filter((t) => t.playoffResult)
    .sort((a, b) => {
      const pa = PLAYOFF_RANK[a.playoffResult] ?? 9;
      const pb = PLAYOFF_RANK[b.playoffResult] ?? 9;
      if (pa !== pb) return pa - pb;
      return ((getRecord(b) || {}).wins || 0) - ((getRecord(a) || {}).wins || 0);
    });

  const row = (t, rank) => {
    const rec = getRecord(t);
    return (
      <button
        key={t.rosterId}
        onClick={() => setPopupTeam(t)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 10, textAlign: 'left',
          padding: '7px 10px', borderRadius: 10, marginBottom: 4,
          background: highlighted(t) ? 'var(--ft-brand-soft)' : 'transparent',
        }}
      >
        <span style={{ fontSize: 12, color: 'var(--ft-ink-3)', width: 18, textAlign: 'right', flex: 'none' }}>{rank}</span>
        <SleeperAvatar id={t.avatar} name={t.ownerName} size={24} />
        <span style={{
          fontSize: 13, flex: 1, minWidth: 0, fontWeight: highlighted(t) ? 600 : 400,
          color: highlighted(t) ? 'var(--ft-ink)' : 'var(--ft-ink-2)',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {t.ownerName}
        </span>
        <span style={{ fontSize: 12, color: 'var(--ft-ink-3)', flex: 'none' }}>
          {rec?.wins ?? '?'}-{rec?.losses ?? '?'}
        </span>
      </button>
    );
  };

  return (
    <div className="ft-section">
      <span className="ft-k">
        {recordSeason ? `${recordSeason} season` : 'Last season'}{league?.name ? ` · ${league.name}` : ''}
      </span>

      {playoffTeams.length > 0 && (
        <div style={{ margin: '14px 0 10px', maxWidth: 520 }}>
          <span className="ft-k" style={{ color: 'var(--ft-ink-3)' }}>Playoffs</span>
          {playoffTeams.map((t) => {
            const rec = getRecord(t);
            const isChamp = t.playoffResult === 'champion';
            return (
              <button
                key={t.rosterId}
                onClick={() => setPopupTeam(t)}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: 11, textAlign: 'left',
                  padding: '10px 13px', borderRadius: 12, marginTop: 6,
                  background: highlighted(t) ? 'var(--ft-brand-soft)' : 'var(--ft-surface)',
                }}
              >
                <SleeperAvatar id={t.avatar} name={t.ownerName} size={30} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {t.ownerName}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--ft-ink-3)' }}>{rec ? `${rec.wins}-${rec.losses}` : ''}</div>
                </div>
                {sideTag(t)}
                <span style={{
                  fontSize: 11, fontWeight: 700, letterSpacing: '.5px', textTransform: 'uppercase', flex: 'none',
                  color: isChamp ? 'var(--ft-brand-ink)' : 'var(--ft-ink-2)',
                }}>
                  {PLAYOFF_LABEL[t.playoffResult] || ''}
                </span>
              </button>
            );
          })}
        </div>
      )}

      <div style={{ marginTop: 16 }}>
        <span className="ft-k" style={{ color: 'var(--ft-ink-3)' }}>Regular season</span>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '0 18px', marginTop: 8 }}>
          <div>{regularSorted.slice(0, mid).map((t, i) => row(t, i + 1))}</div>
          <div>{regularSorted.slice(mid).map((t, i) => row(t, mid + i + 1))}</div>
        </div>
      </div>

      {popupTeam && (
        <TeamBreakdownModal
          team={popupTeam}
          allTeams={allTeams}
          league={league}
          getRecord={getRecord}
          recordSeason={recordSeason}
          regularSorted={regularSorted}
          inTrade={highlighted(popupTeam)}
          onOwner={onOwner}
          onClose={() => setPopupTeam(null)}
        />
      )}
    </div>
  );
}
